import React, { useState } from 'react';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import get from 'lodash/get';
import {
  uploadProductsToCampaignCSV,
  createProductsCSV,
  editProductsCSV,
  updateVariantDescriptorsCSV,
  updateDiscountsCSV,
  updateCartsCSV,
  updateRefillsCartsCSV,
} from 'services/api/admin-csv';
import { formatCsvData, formatError } from 'utils/admin-csv';
import BaseModal from 'shared-components/BaseModal/Modal';
import CloseButton from 'shared-components/CloseButton/CloseButton';
import LoadingScreen from './LoadingScreen';
import FileInputScreen from './FileInputScreen';
import ErrorScreen from './ErrorScreen';
import SelectedFileScreen from './SelectedFileScreen';

import styles from './CSVModal.module.css';

const V2_REQUESTS = [
  'addCarts',
  'removeCarts',
  'addCartsRefills',
  'removeCartsRefills',
];

const getRequest = (type) => {
  const requests = {
    editCampaign: uploadProductsToCampaignCSV,
    addProducts: createProductsCSV,
    editProducts: editProductsCSV,
    updateVariantDescriptors: updateVariantDescriptorsCSV,
    editDiscounts: updateDiscountsCSV,
    addCarts: (csvData) => updateCartsCSV(csvData, false),
    removeCarts: (csvData) => updateCartsCSV(csvData, true),
    addCartsRefills: (csvData) => updateRefillsCartsCSV(csvData, false),
    removeCartsRefills: (csvData) => updateRefillsCartsCSV(csvData, true),
  };
  return requests[type];
};

const CSVModal = ({
  type,
  title,
  campaign,
  onClickClose,
  updateData,
  onUploadSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const clearState = () => {
    setSelectedFile(null);
    setUploading(false);
    setError(null);
  };

  const handleClose = () => {
    clearState();
    onClickClose();
  };

  const handleSelectFile = (event) => setSelectedFile(event.target.files[0]);

  const handleSuccess = async (response) => {
    const { message, value } = response;
    const toastTitle = 'CSV successfully uploaded.';
    onUploadSuccess({ title: toastTitle, message, value });
    clearState();
    onClickClose();
    updateData();
  };

  const handleUnsuccess = (err) => {
    setUploading(false);
    setError(err);
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      const request = getRequest(type);
      const data = formatCsvData(selectedFile);
      const response =
        campaign && campaign.id
          ? await request(campaign.id, data)
          : await request(data);
      if (response.success) {
        handleSuccess(response);
      } else {
        // status code 200 and response.success = false
        const err = {
          value: response.value,
          message: 'All skus successfully updated except for the following:',
        };
        handleUnsuccess(err);
      }
    } catch (e) {
      const err = formatError(selectedFile, e);
      handleUnsuccess(err);
    }
  };

  const handleV2Update = async () => {
    setUploading(true);
    //this request uses v2 of the API endpoint which conforms to Google JSON API
    //we can use this response parsing when other CSV update endpoints are updated
    try {
      const request = getRequest(type);
      const data = formatCsvData(selectedFile);
      const response = await request(data);
      const updatedItemCount = get(response, 'data.updatedItemCount');
      clearState();
      updateData();
      onUploadSuccess(updatedItemCount);
    } catch (err) {
      const uploadError = get(err, 'error');
      setError(uploadError);
    }
    setUploading(false);
  };

  const renderContent = () => {
    const fileSubmitHandler = includes(V2_REQUESTS, type)
      ? handleV2Update
      : handleSubmit;
    if (uploading) return <LoadingScreen type={type} />;
    if (!selectedFile) return <FileInputScreen onChange={handleSelectFile} />;
    if (error)
      return (
        <ErrorScreen
          title={title}
          selectedFile={selectedFile}
          error={error}
          onClearState={clearState}
          onClose={handleClose}
        />
      );
    return (
      <SelectedFileScreen
        title={title}
        selectedFile={selectedFile}
        onClearState={clearState}
        onClose={handleClose}
        onSubmit={fileSubmitHandler}
      />
    );
  };

  return (
    <BaseModal
      isOpen={!!type}
      onRequestClose={handleClose}
      className={styles.modal}
    >
      <div className={styles.container}>
        <CloseButton onClick={handleClose} />
        {renderContent()}
      </div>
    </BaseModal>
  );
};

CSVModal.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string.isRequired,
  campaign: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  onClickClose: PropTypes.func.isRequired,
  updateData: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
};

CSVModal.defaultProps = {
  type: null,
  campaign: {},
};

export default CSVModal;
